/**
 * Admin → Settings → Jev → Metrics & check — E07-F12-T04 (PROTOTYPE).
 *
 * Screens:
 *   jev-metrics    — 30-day metrics per set: decisions, labels, agreement with the
 *                    human, Probable-junk and needs-human shares, cost;
 *   jev-set-check  — «Check set on a row»: pick a set and ONE request row; ONE paid
 *                    Jev call, only after the confirmation dialog; shows the answers
 *                    with probabilities. The result is NOT written to the row.
 */
import { useState } from 'react';
import { Dialog, DialogFooter } from '../ui/index.js';
import { errorMessage } from '../api/client.js';
import {
  useJevCheckQuote,
  useJevCheckRows,
  useJevMetrics,
  useJevQuestionSets,
  useRunJevSetCheck,
  type JevCheckResult,
  type JevSetKey,
  type JevSetMetrics,
} from './jevApi.js';
import {
  ANSWER_KIND_LABEL,
  BAND_LABEL,
  formatDate,
  formatInt,
  formatPct,
  formatUsd,
  type JevBand,
  type JevCheckRow,
  type JevQuestionSet,
} from './jevModel.js';
import {
  BTN_PAID,
  Card,
  FIELD_CLASS,
  Loading,
  Notice,
  Pill,
  ProbBar,
  SectionHeading,
  type PillTone,
} from './jevUi.js';

const BAND_TONE: Record<JevBand, PillTone> = {
  check_first: 'success',
  doubtful: 'warning',
  probable_junk: 'danger',
  needs_human: 'info',
};

// ---------------------------------------------------------------------------
// Metrics
// ---------------------------------------------------------------------------

function Kpi({
  label,
  value,
  testId,
}: {
  readonly label: string;
  readonly value: string;
  readonly testId?: string;
}): React.JSX.Element {
  return (
    <div className="rounded-lg border border-border bg-white px-4 py-3">
      <div className="text-[11px] uppercase tracking-wide text-text-muted">{label}</div>
      <div data-testid={testId} className="mt-1 text-2xl font-semibold text-text-strong">
        {value}
      </div>
    </div>
  );
}

function MetricsRow({
  m,
  set,
}: {
  readonly m: JevSetMetrics;
  readonly set: JevQuestionSet | undefined;
}): React.JSX.Element {
  const title = set?.title ?? m.setKey;
  if (m.decisions === 0) {
    return (
      <tr className="border-b border-border" data-testid={`jev-metrics-${m.setKey}`}>
        <td className="py-3 pr-3">
          <div className="font-medium text-text-strong">{title}</div>
          <code className="text-[10px] text-gray-500">{m.setKey}</code>
        </td>
        <td
          colSpan={6}
          className="py-3 text-xs text-text-muted"
          data-testid={`jev-metrics-${m.setKey}-empty`}
        >
          <i className="fa-regular fa-folder-open mr-1.5" aria-hidden />
          No decisions in the last 30 days — this set has not been used yet.
        </td>
      </tr>
    );
  }
  const junkApplies = m.setKey === 'research_triage';
  const needsHumanApplies = m.setKey === 'research_verdict';
  return (
    <tr className="border-b border-border" data-testid={`jev-metrics-${m.setKey}`}>
      <td className="py-3 pr-3">
        <div className="font-medium text-text-strong">{title}</div>
        <code className="text-[10px] text-gray-500">{m.setKey}</code>
      </td>
      <td className="py-3 pr-3 text-right font-mono">{formatInt(m.decisions)}</td>
      <td className="py-3 pr-3 text-right font-mono">{formatInt(m.labels)}</td>
      <td className="py-3 pr-3">
        <div className="flex items-center justify-end gap-2">
          <span className="font-mono font-semibold text-text-strong">{formatPct(m.agreement)}</span>
          <div className="hidden w-16 sm:block">
            <ProbBar p={m.agreement ?? 0} top />
          </div>
        </div>
      </td>
      <td className="py-3 pr-3 text-right font-mono">
        {junkApplies ? formatPct(m.junkShare) : '—'}
      </td>
      <td className="py-3 pr-3 text-right font-mono">
        {needsHumanApplies ? formatPct(m.needsHumanShare) : '—'}
      </td>
      <td className="py-3 text-right font-mono">{formatUsd(m.costUsd)}</td>
    </tr>
  );
}

function MetricsCard({ sets }: { readonly sets: readonly JevQuestionSet[] }): React.JSX.Element {
  const metricsQuery = useJevMetrics();
  if (metricsQuery.isLoading) return <Loading testId="jev-metrics-loading" />;
  if (metricsQuery.isError || !metricsQuery.data) {
    return (
      <Notice tone="danger">{errorMessage(metricsQuery.error, 'Failed to load metrics')}</Notice>
    );
  }
  const d = metricsQuery.data;
  const decisions = d.sets.reduce((n, s) => n + s.decisions, 0);
  const labels = d.sets.reduce((n, s) => n + s.labels, 0);
  const cost = d.sets.reduce((n, s) => n + s.costUsd, 0);
  return (
    <Card testId="jev-metrics">
      <SectionHeading
        title="Last 30 days"
        hint={`${formatDate(d.fromDate)} – ${formatDate(d.toDate)}. Agreement counts only decisions a human labelled by opening the row; batch approvals by grade are not labels.`}
      />
      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <Kpi label="Decisions" value={formatInt(decisions)} testId="jev-kpi-decisions" />
        <Kpi label="Human labels" value={formatInt(labels)} testId="jev-kpi-labels" />
        <Kpi label="Cost" value={formatUsd(cost)} testId="jev-kpi-cost" />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[44rem] text-sm">
          <thead className="text-[11px] text-text-muted">
            <tr className="border-b border-border">
              <th className="py-2 pr-3 text-left font-medium">Set</th>
              <th className="py-2 pr-3 text-right font-medium">Decisions</th>
              <th className="py-2 pr-3 text-right font-medium">Labels</th>
              <th className="py-2 pr-3 text-right font-medium">Agrees with human</th>
              <th className="py-2 pr-3 text-right font-medium">Probable junk</th>
              <th className="py-2 pr-3 text-right font-medium">Needs human</th>
              <th className="py-2 text-right font-medium">Cost</th>
            </tr>
          </thead>
          <tbody>
            {d.sets.map((m) => (
              <MetricsRow key={m.setKey} m={m} set={sets.find((s) => s.key === m.setKey)} />
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Set check on one row
// ---------------------------------------------------------------------------

function CheckResult({
  result,
  row,
}: {
  readonly result: JevCheckResult;
  readonly row: JevCheckRow;
}): React.JSX.Element {
  return (
    <div
      data-testid="jev-check-result"
      className="grid gap-4 rounded-lg border border-border bg-[var(--color-surface-muted)] p-4"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm">
          <span className="font-semibold text-text-strong">{row.company}</span>{' '}
          <span className="text-text-muted">· {row.domain}</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {result.band ? (
            <Pill tone={BAND_TONE[result.band]}>{BAND_LABEL[result.band]}</Pill>
          ) : null}
          <span className="text-xs text-text" data-testid="jev-check-reason">
            {result.reason}
          </span>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        {result.answers.map((a) => (
          <div
            key={a.question}
            className="rounded-lg border border-border bg-white p-3"
            data-testid={`jev-check-answer-${a.question}`}
          >
            <div className="mb-2 flex items-center justify-between gap-2">
              <code className="truncate text-xs font-semibold text-text-strong">{a.question}</code>
              <span className="text-[10px] text-text-muted">{ANSWER_KIND_LABEL[a.kind]}</span>
            </div>
            <div className="mb-2 text-sm">
              <span className="font-semibold text-primary">{a.answer}</span>{' '}
              <span className="font-mono text-xs text-text-muted">
                confidence {a.confidence.toFixed(2)}
              </span>
            </div>
            <div className="grid gap-1">
              {a.probabilities.map((p) => (
                <div
                  key={p.key}
                  className="grid grid-cols-[6.5rem_minmax(0,1fr)_2.5rem] items-center gap-2 text-[11px]"
                >
                  <span
                    className={`truncate font-mono ${p.key === a.answer ? 'font-semibold text-text-strong' : 'text-text-muted'}`}
                  >
                    {p.key}
                  </span>
                  <ProbBar p={p.p} top={p.key === a.answer} />
                  <span className="text-right font-mono text-text-muted">{p.p.toFixed(2)}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-3 text-[11px] text-text-muted">
        <span>
          Cost <strong className="text-text-strong">{formatUsd(result.costUsd)}</strong> ·{' '}
          {(result.latencyMs / 1000).toFixed(1)} s · state {formatInt(result.stateChars)} chars ·
          logged in ai_call_log
        </span>
        <span
          data-testid="jev-check-not-written"
          className="inline-flex items-center gap-1 font-medium text-text-strong"
        >
          <i className="fa-solid fa-eye" aria-hidden /> Preview only — not written to the request
          row
        </span>
      </div>
    </div>
  );
}

function SetCheckCard({
  sets,
  jevReady,
}: {
  readonly sets: readonly JevQuestionSet[];
  readonly jevReady: boolean;
}): React.JSX.Element {
  const rowsQuery = useJevCheckRows();
  const run = useRunJevSetCheck();
  const [setKey, setSetKey] = useState<JevSetKey>('research_triage');
  const [rowId, setRowId] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const quote = useJevCheckQuote(confirmOpen ? setKey : null);

  const rows = rowsQuery.data ?? [];
  const row = rows.find((r) => r.id === rowId);
  const resultRow = run.data ? rows.find((r) => r.id === run.data.rowId) : undefined;
  const set = sets.find((s) => s.key === setKey);

  function confirm(): void {
    if (!rowId) return;
    setConfirmOpen(false);
    run.mutate({ setKey, rowId });
  }

  return (
    <Card testId="jev-set-check">
      <SectionHeading
        title="Check a set on one row"
        hint="Ask Jev one set on one real request row to see how it answers right now — e.g. after editing questions. One paid call per press; the answer is shown here and never changes the row."
      />
      {!jevReady ? (
        <div className="mb-4">
          <Notice tone="warning">
            Jev is not configured — the check is disabled until a key is saved and Jev is on.
          </Notice>
        </div>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-[16rem_minmax(0,1fr)]">
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-text-strong">Question set</span>
          <select
            data-testid="jev-check-set"
            className={FIELD_CLASS}
            value={setKey}
            onChange={(e) => {
              setSetKey(e.target.value as JevSetKey);
              run.reset();
            }}
          >
            {sets.map((s) => (
              <option key={s.key} value={s.key}>
                {s.title} ({s.key})
              </option>
            ))}
          </select>
          {set ? (
            <span className="mt-1 block text-[11px] text-text-muted">
              {set.questions.length} question{set.questions.length === 1 ? '' : 's'} ·{' '}
              {set.calibrated ? 'calibrated' : 'not calibrated'}
            </span>
          ) : null}
        </label>

        <fieldset>
          <legend className="mb-1 block text-xs font-medium text-text-strong">Row</legend>
          {rowsQuery.isLoading ? <Loading testId="jev-check-rows-loading" /> : null}
          <div
            className="grid max-h-64 gap-1 overflow-y-auto rounded border border-border p-1"
            role="radiogroup"
          >
            {rows.map((r) => {
              const selected = r.id === rowId;
              return (
                <label
                  key={r.id}
                  data-testid={`jev-check-row-${r.id}`}
                  className={`flex cursor-pointer items-start gap-3 rounded px-3 py-2 text-xs ${selected ? 'bg-amber-50 ring-1 ring-[var(--color-accent)]' : 'hover:bg-gray-50'}`}
                >
                  <input
                    type="radio"
                    name="jev-check-row"
                    className="mt-0.5"
                    checked={selected}
                    onChange={() => {
                      setRowId(r.id);
                      run.reset();
                    }}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block font-medium text-text-strong">
                      {r.company} <span className="font-normal text-text-muted">· {r.domain}</span>
                    </span>
                    <span className="block truncate text-[11px] text-text-muted">
                      {r.city}, {r.state} · {r.employees} employees · {r.requestId} {r.requestTitle}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
        </fieldset>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-end gap-3">
        {run.isPending ? (
          <span className="text-xs text-text-muted" data-testid="jev-check-running">
            <i className="fa-solid fa-spinner fa-spin mr-1.5" aria-hidden />
            Waiting for Jev (up to 15 s)…
          </span>
        ) : null}
        <button
          type="button"
          data-testid="jev-check-run"
          disabled={!jevReady || !row || run.isPending}
          onClick={() => setConfirmOpen(true)}
          className={BTN_PAID}
        >
          <i className="fa-solid fa-coins" aria-hidden />
          Check set on this row…
        </button>
      </div>

      {run.isError ? (
        <div className="mt-4">
          <Notice tone="danger" testId="jev-check-error">
            {errorMessage(run.error, 'Jev check failed')}
          </Notice>
        </div>
      ) : null}
      {run.data && resultRow ? (
        <div className="mt-4">
          <CheckResult result={run.data} row={resultRow} />
        </div>
      ) : null}

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Make one paid Jev call?"
        testId="jev-check-confirm"
        maxWidth="md"
        footer={
          <DialogFooter
            onCancel={() => setConfirmOpen(false)}
            onConfirm={confirm}
            confirmDisabled={!quote.data}
            confirmLabel={quote.data ? `Call Jev — ${formatUsd(quote.data.costUsd)}` : 'Call Jev'}
            variant="accent"
            testId="jev-check-confirm"
          />
        }
      >
        <div className="grid gap-3 text-sm text-text">
          <p>
            Jev will answer <code className="rounded bg-gray-100 px-1 text-xs">{setKey}</code> for{' '}
            <strong>{row?.company}</strong> ({row?.domain}). This is exactly one call, without
            retries.
          </p>
          <div className="rounded-lg bg-[var(--color-surface-muted)] p-3 text-center">
            <div
              className="text-xl font-semibold text-primary"
              data-testid="jev-check-confirm-price"
            >
              {quote.data ? formatUsd(quote.data.costUsd) : '…'}
            </div>
            <div className="text-[11px] text-text-muted">
              {quote.data?.isEstimate
                ? 'estimate — this set has no call history yet'
                : 'average price of this set’s calls'}
            </div>
          </div>
          <p className="text-xs text-text-muted">
            The answers and probabilities are shown on this page only. The request row, its triage
            band and its grade stay as they are.
          </p>
        </div>
      </Dialog>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Root
// ---------------------------------------------------------------------------

export function JevMetrics({ jevReady }: { readonly jevReady: boolean }): React.JSX.Element {
  const setsQuery = useJevQuestionSets();
  if (setsQuery.isLoading) return <Loading testId="jev-metrics-sets-loading" />;
  const sets = setsQuery.data ?? [];
  return (
    <div data-testid="jev-metrics-screen" className="grid gap-4">
      <MetricsCard sets={sets} />
      <SetCheckCard sets={sets} jevReady={jevReady} />
    </div>
  );
}
